import {
  X,
  LogOut,
  ChevronDown,
  Sun,
  Moon,
  Crown,
  Settings,
  User,
  Bell,
  Palette,
  Zap,
  Shield,
  Database,
  Globe,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@core/button";
import { Text } from "@core/text";
import Modal from "@core/modal";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";
import { useEffect, useRef, useState } from "react";
import swannIcon from "@packages/assets/swann-icon.png";
import premiumModelBg from "@packages/assets/premiumModelBg2.jpg";
import MyProfileView from "@features/profiles/view/my-profiles-view";
import MyProfileViewModelInside from "@features/profiles/view/my-profiles-view-model-inside";

interface LanguageSelectorProps {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
}

const LanguageSelector = ({
  selectedLanguage,
  onLanguageChange,
}: LanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Language options with flags
  const languages = [
    { code: "en", name: "English", flag: enFlag },
    { code: "tr", name: "Türkçe", flag: trFlag },
    { code: "de", name: "Deutsch", flag: deFlag },
    { code: "fr", name: "Français", flag: frFlag },
  ];

  const selectedLang =
    languages.find((lang) => lang.code === selectedLanguage) || languages[0];

  return (
    <div className="relative w-full" ref={popoverRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-3 bg-gray-50 dark:bg-dark-600 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-500 transition-colors"
      >
        <div className="flex items-center gap-2">
          <img
            src={selectedLang.flag}
            alt={selectedLang.name}
            className="w-5 h-5 rounded-full object-cover shadow-sm"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {selectedLang.name}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden z-[300] border border-gray-200 dark:border-gray-600">
          <div className="py-1">
            {languages.map((lang) => (
              <div
                key={lang.code}
                onClick={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
                className={`
                  flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer transition-colors
                  ${
                    lang.code === selectedLanguage
                      ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }
                `}
              >
                <img
                  src={lang.flag}
                  alt={lang.name}
                  className="w-4 h-4 rounded-full object-cover shadow-sm"
                />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isPremium: boolean;
  isSchool: boolean;
  theme: string;
  toggleTheme: () => void;
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
  onLogout: () => void;
  onShowProModal: () => void;
}

const SettingsModal = ({
  isOpen,
  onClose,
  isPremium,
  isSchool,
  theme,
  toggleTheme,
  selectedLanguage,
  onLanguageChange,
  onLogout,
  onShowProModal,
}: SettingsModalProps) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("general");

  const sidebarItems = [
    {
      id: "general",
      label: t("settings.general", "General"),
      icon: <Settings size={18} />,
    },
    {
      id: "appearance",
      label: t("settings.appearance", "Appearance"),
      icon: <Palette size={18} />,
    },
    {
      id: "language",
      label: t("settings.language", "Language"),
      icon: <Globe size={18} />,
    },
    {
      id: "account",
      label: t("settings.account", "Account"),
      icon: <User size={18} />,
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "general":
        return (
          <div className="p-4 lg:p-6 space-y-6">
            <div>
              <Text
                variant="h3"
                className="font-semibold text-gray-900 dark:text-white mb-2"
              >
                {t("settings.general", "General")}
              </Text>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t(
                  "settings.general.description",
                  "Manage your general preferences and settings",
                )}
              </Text>
            </div>

            {/* Pro Membership Section */}
            {isPremium || isSchool ? (
              <div
                className="relative rounded-xl overflow-hidden"
                style={{
                  backgroundImage: `url(${premiumModelBg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  aspectRatio: "20/9",
                }}
              >
                {/* Overlay for better text readability */}
                <div className="absolute inset-0 bg-black/50 dark:bg-black/60" />

                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-6">
                  <img
                    src={swannIcon}
                    alt="Swann logo"
                    className="w-32 h-32 rounded-lg mb-[-20px] mt-[-30px]"
                  />
                  <Text variant="h4" className="font-bold text-white mb-2">
                    {t("settings.pro.active.title", "Premium Aktif")}
                  </Text>
                  <Text variant="p" className="text-white/90 text-sm">
                    {t(
                      "settings.pro.active.description",
                      "Tüm premium özelliklere erişiminiz bulunuyor",
                    )}
                  </Text>
                </div>
              </div>
            ) : (
              <div
                onClick={() => {
                  onShowProModal();
                  onClose();
                }}
                className="relative rounded-xl cursor-pointer hover:opacity-90 transition-opacity group overflow-hidden border border-gray-200 dark:border-dark-400"
                style={{
                  backgroundImage: `url(${premiumModelBg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  aspectRatio: "20/9",
                }}
              >
                {/* Overlay for better text readability */}
                <div className="absolute inset-0 bg-black/40 dark:bg-black/50" />

                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-6">
                  {/* Swann icon is hidden on mobile */}
                  <img
                    src={swannIcon}
                    alt="Swann logo"
                    className="w-32 h-32 rounded-lg mb-[-20px] mt-[-30px] hidden sm:block"
                  />
                  <Text variant="h4" className="font-bold text-white mb-2">
                    {t("settings.becomeMadlener", "Swann Premium'a geç")}
                  </Text>
                  <Text
                    variant="p"
                    className="text-white/90 text-sm mb-4 max-w-xs"
                  >
                    {t(
                      "settings.madlenerDescription",
                      "Sınırsız özellikler ve gelişmiş araçlarla eğitim deneyiminizi zenginleştirin.",
                    )}
                  </Text>
                  {/* Upgrade button is hidden on mobile */}
                  <div className="bg-white rounded-lg px-6 py-2 hover:bg-gray-100 transition-colors hidden sm:block">
                    <Text
                      variant="p"
                      className="dark:text-black text-sm font-medium"
                    >
                      {t("settings.upgradeToPro", "Premium Ol")}
                    </Text>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "appearance":
        return (
          <div className="p-4 lg:p-6 space-y-6">
            <div>
              <Text
                variant="h3"
                className="font-semibold text-gray-900 dark:text-white mb-2"
              >
                {t("settings.appearance", "Appearance")}
              </Text>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t(
                  "settings.appearance.description",
                  "Customize the look and feel of your workspace",
                )}
              </Text>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-600 rounded-lg opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-3">
                  {theme === "dark" ? (
                    <Moon
                      size={20}
                      className="text-gray-600 dark:text-gray-300"
                    />
                  ) : (
                    <Sun
                      size={20}
                      className="text-gray-600 dark:text-gray-300"
                    />
                  )}
                  <div>
                    <Text
                      variant="p"
                      className="font-medium text-gray-900 dark:text-white"
                    >
                      {t("settings.theme", "Theme")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400"
                    >
                      {theme === "dark"
                        ? t("settings.theme.dark", "Dark mode is enabled")
                        : t("settings.theme.light", "Light mode is enabled")}
                    </Text>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Text
                    variant="p"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    {t("settings.theme.dark.label", "Dark")}
                  </Text>
                  <div
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      theme === "dark" ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transform transition-all duration-300 ${
                        theme === "dark" ? "translate-x-6" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Light menu not supported message */}
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <Text
                  variant="p"
                  className="text-sm text-yellow-800 dark:text-yellow-200"
                >
                  {t(
                    "settings.theme.light.notSupported",
                    "Light menu is not yet supported",
                  )}
                </Text>
              </div>
            </div>
          </div>
        );

      case "language":
        return (
          <div className="p-4 lg:p-6 space-y-6">
            <div>
              <Text
                variant="h3"
                className="font-semibold text-gray-900 dark:text-white mb-2"
              >
                {t("settings.language", "Language")}
              </Text>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t(
                  "settings.language.description",
                  "Choose your preferred language for the interface",
                )}
              </Text>
            </div>

            <div className="space-y-4">
              <div>
                <Text
                  variant="p"
                  className="font-medium text-gray-900 dark:text-white mb-3"
                >
                  {t("settings.interfaceLanguage", "Interface Language")}
                </Text>
                <LanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={onLanguageChange}
                />
              </div>

              <div className="hidden">
                <Text
                  variant="p"
                  className="font-medium text-gray-900 dark:text-white mb-3"
                >
                  {t("settings.contentLanguage", "Content Language")}
                </Text>
                <LanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={onLanguageChange}
                />
                <Text
                  variant="p"
                  className="text-sm text-gray-500 dark:text-gray-400 mt-2"
                >
                  {t(
                    "settings.contentLanguage.description",
                    "For best results, select the language you mainly speak. If it's not listed, it may still be supported via auto-detection.",
                  )}
                </Text>
              </div>
            </div>
          </div>
        );

      case "account":
        return (
          <div className="h-full max-h-full lg:max-h-[600px] overflow-y-auto custom-scrollbar">
            <MyProfileViewModelInside
              onLogout={() => {
                onLogout();
                onClose();
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      bare={true}
      outerClassName="max-w-5xl lg:max-w-5xl max-w-full h-[100vh] lg:h-auto"
      hideCloseButton={true}
    >
      <div className="relative h-full lg:h-auto h-[100vh] shadow-2xl shadow-gray-200/20 dark:shadow-black/40">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white dark:bg-dark-600 rounded-full shadow-md lg:shadow-none lg:bg-transparent lg:dark:bg-dark-600"
        >
          <X size={20} className="text-gray-600 dark:text-gray-300" />
        </button>

        {/* Mobile Layout */}
        <div className="lg:hidden h-[100vh] flex flex-col bg-white dark:bg-dark-500">
          {/* Mobile Header with tabs */}
          <div className="bg-gray-50 dark:bg-dark-600 p-4 pt-16">
            <Text
              variant="h2"
              className="font-bold text-gray-900 dark:text-white mb-4"
            >
              {t("settings.title", "Settings")}
            </Text>

            {/* Mobile tabs - horizontal scroll */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeSection === item.id
                      ? "bg-white dark:bg-dark-400 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-dark-400"
                  }`}
                >
                  <span
                    className={
                      activeSection === item.id
                        ? "text-blue-600 dark:text-white"
                        : ""
                    }
                  >
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Content */}
          <div className="flex-1 overflow-y-auto h-full">{renderContent()}</div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid grid-cols-3 min-h-[600px]">
          {/* Left Sidebar */}
          <div className="bg-gray-50 dark:bg-dark-600 p-6 rounded-l-xl">
            <div className="mb-6">
              <Text
                variant="h2"
                className="font-bold text-gray-900 dark:text-white"
              >
                {t("settings.title", "Settings")}
              </Text>
            </div>

            <nav className="space-y-1">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    activeSection === item.id
                      ? "bg-white dark:bg-dark-400 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-dark-400"
                  }`}
                >
                  <span
                    className={
                      activeSection === item.id
                        ? "text-blue-600 dark:text-white"
                        : ""
                    }
                  >
                    {item.icon}
                  </span>
                  <Text variant="p" className="font-medium">
                    {item.label}
                  </Text>
                </button>
              ))}
            </nav>
          </div>

          {/* Right Content */}
          <div className="col-span-2 bg-white dark:bg-dark-500 rounded-r-xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SettingsModal;
